// @ts-check
import { jsonToHTML, MarkType, NodeType } from '@contentstack/utils';
import React from 'react';
import Link from '~/components/Common/Link';
import { ConstructBasicElement, ConstructBasicMark, ConstructCustomElement } from './constructors';
import { enumerateKeys } from './contentStackOverrides';

/** @typedef {(node: import('@contentstack/utils').Node, next: import('@contentstack/utils').Next) => React.ReactNode} RenderElementByNode */
/** @typedef {(text: string) => React.ReactNode} RenderMark */
/** @typedef {Record<string, RenderMark | RenderElementByNode>} RenderOptionEntry */
/** @typedef {RenderOptionEntry & { localIndex: number }} RenderOption */
/** @typedef {import('@contentstack/utils').EntryEmbedable} EntryEmbedable */
/** @typedef {EntryEmbedable | EntryEmbedable[]} JRTEContent */
/** @typedef {React.ReactNode[]} ToGoBox */

/** @type {( renderOverrides?: RenderOptionEntry ) => RenderOption} */
export const getRenderOptions = (renderOverrides = {}) =>
	Object.assign(
		{
			// Start a local index
			localIndex: 0,
		},
		/** @type {RenderOptionEntry} */
		{
			// Null or basic types.
			...ConstructBasicElement(NodeType.DOCUMENT, null),
			...ConstructCustomElement(NodeType.REFERENCE, (node, next) => {
				// Is valid asset representation
				const isAsset = node.attrs?.type === 'asset' && node.attrs?.['content-type-uid'] === 'sys_assets';
				// Direct asset embed (visual square in JRTE) - same as link with specific fields.
				const isAssetDownload = isAsset && node.attrs?.['display-type'] === 'download';
				// "Asset as Link" - same as link
				const isAssetLink = isAsset && node.attrs?.['display-type'] === 'link';

				if (isAssetDownload) {
					// Only allow HTML + PDF.
					if (!['application/pdf', 'text/html'].includes(node.attrs['asset-type'])) {
						console.warn('Attempting to reference non PDF/HTML asset.');
						return next(node.children);
					}

					return (
						<Link
							className={node.attrs['class-name']}
							openInNewTab={true}
							// "Link" is the property that is manually configured via property "Embed Link" - automatic by CS now?
							to={node.attrs['link'] ?? node.attrs['asset-link']}
						>
							{node.attrs['asset-alt'] ??
								node.attrs['asset-caption'] ??
								"Edit the PDF in the JRTE, and set the 'Alt' or 'Caption' field to set the inline text."}
						</Link>
					);
				}
				if (isAssetLink) {
					return (
						<Link
							className={node.attrs?.['class-name']}
							openInNewTab={!node.attrs?.target?.endsWith('_self')}
							to={node.attrs?.href}
						>
							{next(node.children)}
						</Link>
					);
				}

				console.error('Embedded type not supported:', node.attrs.type);
				return next(node.children);
			}),

			// If we are given a 'default' or 'fragment' key, then it is a React Fragment.
			...ConstructBasicElement('fragment', 'fragment'),
			...ConstructBasicElement('default', 'fragment'),

			// Basic elements
			...ConstructBasicElement('span', 'span'),
			...ConstructBasicElement(NodeType.PARAGRAPH, 'p'),
			...ConstructCustomElement(NodeType.LINK, (node, next) => (
				<Link
					className={node.attrs?.['class-name']}
					openInNewTab={!node.attrs?.target?.endsWith('_self')}
					to={node.attrs?.url}
				>
					{next(node.children)}
				</Link>
			)),
			...ConstructCustomElement(NodeType.IMAGE, (node, next) => (
				<React.Fragment>
					{/* eslint-disable-next-line jsx-a11y/alt-text */}
					<img src={node.attrs?.src || node.attrs?.url} />
					{next(node.children)}
				</React.Fragment>
			)),

			// Headings
			...ConstructBasicElement(NodeType.HEADING_1, 'h1'),
			...ConstructBasicElement(NodeType.HEADING_2, 'h2'),
			...ConstructBasicElement(NodeType.HEADING_3, 'h3'),
			...ConstructBasicElement(NodeType.HEADING_4, 'h4'),
			...ConstructBasicElement(NodeType.HEADING_5, 'h5'),
			...ConstructBasicElement(NodeType.HEADING_6, 'h6'),
			...ConstructCustomElement(NodeType.HR, () => <hr />),

			// Lists
			...ConstructBasicElement(NodeType.ORDER_LIST, 'ol'),
			...ConstructBasicElement(NodeType.UNORDER_LIST, 'ul'),
			...ConstructBasicElement(NodeType.LIST_ITEM, 'li'),

			// Tables
			...ConstructBasicElement(NodeType.TABLE, 'table'),
			...ConstructBasicElement(NodeType.TABLE_HEADER, 'thead'),
			...ConstructBasicElement(NodeType.TABLE_BODY, 'tbody'),
			...ConstructBasicElement(NodeType.TABLE_FOOTER, 'tfoot'),
			...ConstructBasicElement(NodeType.TABLE_ROW, 'tr'),
			...ConstructBasicElement(NodeType.TABLE_HEAD, 'th'),
			...ConstructBasicElement(NodeType.TABLE_DATA, 'td'),

			// Quotes
			...ConstructBasicElement(NodeType.BLOCK_QUOTE, 'blockquote'),
			...ConstructBasicElement(NodeType.CODE, 'code'),

			// Marks
			...ConstructBasicMark(MarkType.BOLD, 'strong'),
			...ConstructBasicMark(MarkType.ITALIC, 'em'),
			...ConstructBasicMark(MarkType.UNDERLINE, 'u'),
			...ConstructBasicMark(MarkType.INLINE_CODE, 'span'),
			...ConstructBasicMark(MarkType.SUBSCRIPT, 'sub'),
			...ConstructBasicMark(MarkType.SUPERSCRIPT, 'sup'),
		},
		renderOverrides
	);

/**
 * @typedef JsonToElementOptions
 * @property {Record<string, JRTEContent>} entry The entry(ies) to enumerate.
 * @property {string[]} paths The paths to enumerate.
 * @property {RenderOption} renderOption The options to render the content with.
 * @property {ToGoBox} toGoBox The array to populate with output.

	* @param {JsonToElementOptions} option
	*/
export function jsonToElement({ entry, paths, renderOption, toGoBox = [] }) {
	const entryArr = Array.isArray(entry) ? entry : [entry];

	for (const entryItem of entryArr) {
		enumerateKeys({
			entry: entryItem,
			paths,
			toGoBox,
			renderOption,
		});
	}
}

/**
 * @param {JsonArg} jsonArg
 * @param {RenderOptionEntry} renderOverrides
 * @returns {React.ReactNode[][]}
 */
function GetCopyFromSingleJson(jsonArg, renderOverrides) {
	if (!!jsonArg && typeof jsonArg === 'object' && !('json' in jsonArg)) {
		// This is semi-incorrect usage, but it's just what we get back from live preview :(
		// @ts-ignore
		jsonArg = { json: jsonArg };
	}

	if (typeof jsonArg.json === 'string') {
		// Update the json so there are nodes to parse.
		jsonArg.json = JSON.parse(jsonArg.json);
	}

	return GetCopyFromEntry(jsonArg, ['json'], renderOverrides);
}

/**
 * @typedef JsonArg
 * @property {string} json
 */
/** @typedef {JsonArg | JsonArg[]} JsonArgs */
/*
 * Parse JRTE json into elements to render.
 * @param {JsonArgs} jsonHolder
 * @param {RenderOptionEntry} [renderOverrides=null]
 * @returns {React.ReactNode[][]}
 */
export function GetCopyFromJson(jsonHolder, renderOverrides = null) {
	if (!jsonHolder) {
		return null;
	}

	if (!Array.isArray(jsonHolder)) {
		jsonHolder = [jsonHolder];
	}

	return [].concat(...jsonHolder.map((j) => GetCopyFromSingleJson(j, renderOverrides)));
}

/**
 * Parse embedded 'copy' block into elements to render.
 * @template {Object & { [key: string]: EntryEmbedable[] }} T
 * @param {T} embeddedCopy
 * @param {(keyof T)[]} keysOfObj
 * @param {RenderOptionEntry} [renderOverrides=null]
 * @returns {React.ReactNode[][]}
 */
export function GetCopyFromEntry(embeddedCopy, keysOfObj, renderOverrides = null) {
	const output = [];
	const renderOption = getRenderOptions(renderOverrides);

	if (!embeddedCopy) {
		return [];
	}

	// We need to only use 'string' from (keyof T) = string | number | Symbol
	/** @type {string[]} */
	// @ts-ignore
	const typedKeys = keysOfObj;

	try {
		jsonToElement({
			entry: Object.assign({}, embeddedCopy),
			paths: typedKeys,
			toGoBox: output,
			renderOption,
		});
	} catch (ex) {
		/** @type {string | T} */
		let html = Object.assign({}, embeddedCopy);
		try {
			jsonToHTML({
				// @ts-ignore
				entry: Object.assign({}, embeddedCopy),
				paths: typedKeys,
			});
		} catch (_inner) {
			html = 'No html';
		}

		console.error('Failure to parse the copy:', html, '\nError:', ex);
		return [[<p>No HTML</p>]];
	}

	return output[0];
}

export * from './constructors';
